/**
 * Fix DESCRIPTORS. This module applies nothing, writes nothing and touches no
 * network — it only describes a remedy in a shape the UI can hand straight to
 * the existing writer.
 *
 * `perPlatformPatch` deliberately mirrors what `aspectRatioPatch()` produces
 * in `src/components/ui/inspector/bodies/post/mediaKeysWriter.ts:123-126`,
 * including the `unset: ['variantUrl']` — that module's header (:39-41) states
 * the rule: "Invalidated (deleted) by every writer here — any framing/aspect/
 * placement change means the bake no longer matches". Reusing the shape means
 * a fix goes through `commitMediaKeysPatches` like every other media_keys
 * change: no new write machinery, no second writer, no race with the
 * 3-writer clobber guard that module exists to provide.
 */
import {
  type PlacementRow,
  getPlacement,
  toMatrixPlatform,
} from '../platform-matrix/placement-matrix.ts';

import type { QualityFix } from './types.ts';

/**
 * The aspect ratio a crop-based fix should target: the RESOLVED placement's
 * recommendation when we have the row (a Story's 9:16 must not be "fixed" to
 * the platform's feed default), falling back to the platform's primary row.
 */
export function fixTargetRatio(
  platform: string,
  row: PlacementRow | undefined,
): string | undefined {
  if (row) return row.recommendedRatio;
  const p = toMatrixPlatform(platform);
  return p ? getPlacement(p)?.recommendedRatio : undefined;
}

/**
 * "Convert to JPEG". The MECHANISM is an aspect override, which is not
 * obvious and is worth stating: `post-dispatcher.ts:394-417` gates
 * `transformMediaSetForPlatform` on `Boolean(perPlatformCfg?.aspectRatio)`,
 * `transform-media.ts:132` uploads the result with contentType 'image/jpeg',
 * and `buildVariantPath` (:259) emits a '.jpg' path. So setting a ratio IS
 * what makes the publish path re-encode the WebP the platform would reject.
 */
export function setCropReencodeJpegFix(
  platform: string,
  row: PlacementRow | undefined,
): QualityFix | null {
  const ratio = fixTargetRatio(platform, row);
  if (!ratio) return null;
  return {
    id: 'set_crop_reencode_jpeg',
    label: 'Convert to JPEG',
    platform,
    perPlatformPatch: { set: { aspectRatio: ratio }, unset: ['variantUrl'] },
  };
}

export function applyRecommendedRatioFix(
  platform: string,
  row: PlacementRow | undefined,
): QualityFix | null {
  const ratio = fixTargetRatio(platform, row);
  if (!ratio) return null;
  return {
    id: 'apply_recommended_ratio',
    label: `Crop to ${ratio}`,
    platform,
    perPlatformPatch: { set: { aspectRatio: ratio }, unset: ['variantUrl'] },
  };
}

/**
 * Trim to the platform's hard ceiling at the last word boundary before the
 * limit — never a raw `slice`, which guillotines mid-word, and never one that
 * splits a surrogate pair (that renders as U+FFFD). The surrogate guard is the
 * same one `clampText` uses in `shared/threads-post-params.ts:73-79`.
 */
export function truncateCaptionToLimit(caption: string, max: number): string {
  if (caption.length <= max) return caption;
  let sliced = caption.slice(0, max);
  const lastCode = sliced.charCodeAt(sliced.length - 1);
  if (lastCode >= 0xd800 && lastCode <= 0xdbff) sliced = sliced.slice(0, -1);
  const lastSpace = sliced.search(/\s\S*$/);
  // Only honor the word boundary if it doesn't throw away most of the caption
  // (a single 3000-char "word" would otherwise trim to nothing).
  const candidate = lastSpace > max * 0.5 ? sliced.slice(0, lastSpace) : sliced;
  return candidate.trimEnd();
}

export function trimCaptionToLimitFix(platform: string, caption: string, max: number): QualityFix {
  return {
    id: 'trim_caption_to_limit',
    label: 'Trim to fit',
    platform,
    captionReplacement: truncateCaptionToLimit(caption, max),
  };
}
